/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { Button } from '@/components';
import { FieldError } from '@/components/ui/field';
import { TextAreaFormField } from '@/domain/termine/features/UpdateTermin/Desktop/components/FormFields/TextAreaFormField';
import { TimeInputFormField } from '@/domain/termine/features/UpdateTermin/Desktop/components/FormFields/TimeInputFormField';
import { CalendarSelectFormField } from '@/domain/termine/features/UpdateTermin/shared/FormFields';
import { ColorPickerFormField } from '@/domain/termine/features/UpdateTermin/shared/FormFields/ColorPickerFormField';
import { DatePickerFormField } from '@/domain/termine/features/UpdateTermin/shared/FormFields/DatePickerFormField';
import { ImageFormField } from '@/domain/termine/features/UpdateTermin/shared/FormFields/ImageFormField';
import { InputFormField } from '@/domain/termine/features/UpdateTermin/shared/FormFields/InputFormField';
import { RepetitionIntervalFormField } from '@/domain/termine/features/UpdateTermin/shared/FormFields/RepetitionIntervalFormField';
import { useCalendarContext } from '@/domain/termine/features/Calendar/shared/CalendarContext';
import { UpdateTermineFormProps } from '@/domain/termine/features/UpdateTermin/shared/types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  ArbeitszeitVerknuepfungCard,
  SchichtVerknuepfungCard,
} from '@/domain/termine/features/UpdateTermin/shared/Verknuepfungen';

export function UpdateTermineFormContent({ form }: UpdateTermineFormProps) {
  const { t } = useTranslation();
  const [showSpecials, setShowSpecials] = useState(false);
  const { termineByDay } = useCalendarContext();
  const { id } = useParams();

  const termin = Array.from(termineByDay.values())
    .flat()
    .find((termin) => termin.id === id);
  const terminId = termin?.id;
  const verknuepfteArbeitszeiten = termin?.arbeitszeiten ?? [];
  const verknuepfteSchichten = termin?.schichten ?? [];

  return (
    <div className="flex flex-col gap-4 p-6 pb-24">
      <span className="font-normal text-neutrals-700 text-xs">
        {t('domain.termine.sheet.pflichtfelder')}
      </span>
      <div className="flex flex-col flex-1 gap-4">
        <ImageFormField form={form} />
        <div className="flex flex-col gap-4 flex-1">
          <div className="flex gap-4 items-start">
            <InputFormField form={form} />
            <ColorPickerFormField form={form} />
          </div>
          <div className="flex flex-col md:flex-row gap-4">
            <DatePickerFormField
              name="datum"
              form={form}
              disabled={
                (termin?.schichten && termin.schichten.length > 0) ||
                (termin?.arbeitszeiten && termin.arbeitszeiten.length > 0)
              }
            />
            <div className="flex flex-col">
              <div className="flex gap-4">
                <TimeInputFormField name="beginn" form={form} />
                <TimeInputFormField name="ende" form={form} />
              </div>
              <form.Subscribe selector={(state) => [state.fieldMeta]}>
                {([fieldMeta]) => {
                  const timeErrors =
                    fieldMeta.beginn?.errors ?? fieldMeta.ende?.errors;
                  return <FieldError errors={timeErrors} />;
                }}
              </form.Subscribe>
            </div>
          </div>
          <div className="flex flex-col gap-4 w-full">
            <RepetitionIntervalFormField form={form} />
          </div>
          <CalendarSelectFormField form={form} />
          <TextAreaFormField form={form} name="beschreibung" />

          {!showSpecials && (
            <Button
              type="button"
              variant="ghost-primary"
              className="self-start w-auto"
              onClick={() => setShowSpecials(true)}>
              <span className="text-sm">
                {t('domain.termine.addAdditionalInformation')}
              </span>
            </Button>
          )}
          {showSpecials && <TextAreaFormField form={form} name="specials" />}
          {terminId && verknuepfteSchichten.length > 0 && (
            <div className="flex flex-col gap-2">
              <span className="text-xs font-bold text-neutrals-900">
                {t('domain.termine.form.verknuepfteSchichten')}
              </span>
              {verknuepfteSchichten.map((schicht) => (
                <SchichtVerknuepfungCard
                  key={schicht.id}
                  schichten={schicht}
                  terminId={terminId}></SchichtVerknuepfungCard>
              ))}
            </div>
          )}
          {terminId && verknuepfteArbeitszeiten.length > 0 && (
            <div className="flex flex-col gap-2">
              <span className="text-xs font-bold text-neutrals-900">
                {t('domain.termine.form.verknuepfteArbeitszeiten', {
                  count: verknuepfteArbeitszeiten.length,
                })}
              </span>
              {verknuepfteArbeitszeiten.map((arbeitszeit) => (
                <ArbeitszeitVerknuepfungCard
                  key={arbeitszeit.id}
                  arbeitszeit={arbeitszeit}
                  terminId={terminId}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
